/**
 * MessagesOverlay - Mobile slide-in overlay for the Messages experience.
 *
 * Two stacked `SlideOverlay`s give a native push-style flow:
 *  - Outer overlay shows the conversation list (back button closes the whole
 *    Messages experience).
 *  - When a conversation is selected, an inner overlay slides in on top
 *    showing just the thread (back button returns to the list).
 *
 * Selection is managed locally so the URL never changes; the `/messages` route
 * still owns deep links and the bottom-tab destination. Club chats fall back
 * to navigation since they live under `/clubs/:id?tab=chat`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/auth.reducer';
import { getConversations } from '../helpers/messagesHelpers';
import SlideOverlay from './SlideOverlay';
import ConversationList from '../components/Messages/ConversationList';
import ConversationThread from '../components/Messages/ConversationThread';
import GroupConversationThread from '../components/Messages/GroupConversationThread';
import NewMessageModal from './NewMessageModal';
import AddPeopleToChatModal from './AddPeopleToChatModal';
import GroupChatSettingsModal from './GroupChatSettingsModal';
import { useSocket } from '../utils/SocketContext';

function MessagesOverlay({ open, onClose }) {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showAddPeopleModal, setShowAddPeopleModal] = useState(false);
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false);
  const { connected, subscribe } = useSocket();

  const loadConversations = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    getConversations(true)
      .then((data) => {
        if (!cancelled) setConversations(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    return loadConversations();
  }, [open, loadConversations]);

  useEffect(() => {
    if (!open || !connected) return undefined;
    const unsubscribers = [
      subscribe('new_message', loadConversations),
      subscribe('new_group_message', loadConversations),
      subscribe('club_chat_message', loadConversations),
    ];
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [open, connected, subscribe, loadConversations]);

  const markConversationReadLocally = useCallback((conv) => {
    setConversations((prev) =>
      prev.map((item) => {
        const sameConversation =
          (conv.type === 'club' && item.type === 'club' && item.club_id === conv.club_id) ||
          (conv.type === 'group' && item.type === 'group' && item.group_conversation_id === conv.group_conversation_id) ||
          (conv.type !== 'club' && conv.type !== 'group' && item.type === 'dm' && item.conversation_id === conv.conversation_id);
        return sameConversation ? { ...item, is_unread: false } : item;
      })
    );
  }, []);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setShowNewMessageModal(false);
      setShowAddPeopleModal(false);
      setShowGroupSettingsModal(false);
    }
  }, [open]);

  const handleSelect = (conv) => {
    markConversationReadLocally(conv);
    if (conv.type === 'club') {
      onClose?.();
      navigate(`/clubs/${conv.club_id}?tab=chat`);
      return;
    }
    setSelected(conv);
  };

  const handleCloseThread = () => {
    setSelected(null);
  };

  const handleNewMessageSuccess = (conv) => {
    if (conv?.group_conversation_id) {
      const groupConv = { ...conv, type: 'group' };
      setConversations((prev) => [...prev, groupConv]);
      setSelected(groupConv);
    } else {
      const dm = { ...conv, type: 'dm' };
      setConversations((prev) => [...prev, dm]);
      setSelected(dm);
    }
  };

  const handleAddPeopleSuccess = (updatedConv) => {
    if (!updatedConv) return;
    const groupConv = { ...updatedConv, type: 'group' };
    setSelected(groupConv);
    setConversations((prev) => {
      const existing = prev.find(
        (c) => c.type === 'group' && c.group_conversation_id === groupConv.group_conversation_id
      );
      if (existing) {
        return prev.map((c) =>
          c.type === 'group' && c.group_conversation_id === groupConv.group_conversation_id
            ? groupConv
            : c
        );
      }
      return [...prev, groupConv];
    });
  };

  const threadTitle = selected
    ? selected.type === 'group'
      ? (selected.display_name || 'Group')
      : (selected.other_user?.displayname || selected.other_user?.username || 'Direct message')
    : '';

  // Inner thread overlay only stays open when there is a non-club selection.
  const threadOpen = open && (selected?.type === 'dm' || selected?.type === 'group');

  return (
    <>
      <SlideOverlay
        open={open}
        title="Messages"
        onClose={onClose}
        testId="messages-overlay"
        zIndex={40}
      >
        <div className="flex flex-col">
          <ConversationList
            conversations={conversations}
            loading={loading}
            onSelect={handleSelect}
            onNewMessage={() => setShowNewMessageModal(true)}
          />
        </div>
      </SlideOverlay>
      <SlideOverlay
        open={threadOpen}
        title={threadTitle}
        onClose={handleCloseThread}
        testId="messages-thread-overlay"
        zIndex={45}
        contentClassName="flex flex-1 min-h-0 flex-col overflow-hidden"
      >
        {selected?.type === 'dm' ? (
          <ConversationThread
            conversationId={selected.conversation_id}
            otherUser={selected.other_user}
            onAddPeople={() => setShowAddPeopleModal(true)}
          />
        ) : selected?.type === 'group' ? (
          <GroupConversationThread
            groupConversationId={selected.group_conversation_id}
            groupConversation={selected}
            onSettings={() => setShowGroupSettingsModal(true)}
          />
        ) : null}
      </SlideOverlay>
      {showNewMessageModal && (
        <NewMessageModal
          currentUserId={currentUser?.user_id}
          onClose={() => setShowNewMessageModal(false)}
          onStartConversation={(conv) => {
            setShowNewMessageModal(false);
            handleNewMessageSuccess(conv);
          }}
        />
      )}
      {showAddPeopleModal && (
        <AddPeopleToChatModal
          mode={selected?.type === 'group' ? 'group_add' : 'dm_to_group'}
          currentUserId={currentUser?.user_id}
          otherUserId={selected?.type === 'dm' ? selected?.other_user?.user_id : null}
          groupConversation={selected?.type === 'group' ? selected : null}
          onClose={() => setShowAddPeopleModal(false)}
          onSuccess={(updatedConv) => {
            setShowAddPeopleModal(false);
            handleAddPeopleSuccess(updatedConv);
          }}
        />
      )}
      {showGroupSettingsModal && selected?.type === 'group' && (
        <GroupChatSettingsModal
          groupConversation={selected}
          currentUserId={currentUser?.user_id}
          onClose={() => setShowGroupSettingsModal(false)}
          onSuccess={(updatedConv) => {
            setShowGroupSettingsModal(false);
            if (updatedConv == null) {
              setSelected(null);
              setConversations((prev) =>
                prev.filter(
                  (c) => !(c.type === 'group' && c.group_conversation_id === selected.group_conversation_id)
                )
              );
            } else {
              const groupConv = { ...updatedConv, type: 'group' };
              setSelected(groupConv);
              setConversations((prev) =>
                prev.map((c) =>
                  c.type === 'group' && c.group_conversation_id === groupConv.group_conversation_id
                    ? groupConv
                    : c
                )
              );
            }
          }}
        />
      )}
    </>
  );
}

export default MessagesOverlay;
